import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { SheetFooter, SheetHeader } from './pdf/PdfChrome';
import { C, base, pdfImage, today } from './pdf/pdfTheme';

// The gym sheet: one row per exercise with what to do and six sessions to write the load in,
// then a page of how-tos. Landscape, so the session columns get room.

const SESSIONS = 6;
const WARMUP_SUFFIX = /\s*\((warmup|warm-up|aquecimento)\)/i;

const s = StyleSheet.create({
  page: { ...base.page },
  headRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingBottom: 5,
    borderBottomWidth: 0.75,
    borderBottomColor: C.ink,
  },
  th: { fontSize: 6.5, fontWeight: 700, color: C.ink2, letterSpacing: 0.5, textTransform: 'uppercase' },
  thCenter: { textAlign: 'center' },
  thSession: { width: 58, marginLeft: 6, alignItems: 'center' },
  thDate: { marginTop: 9, width: 40, borderBottomWidth: 0.6, borderBottomColor: C.field },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: C.line,
  },
  num: { width: 18, fontSize: 10, fontWeight: 900, color: C.green },
  thumb: { width: 46, height: 34, marginRight: 9, borderRadius: 4, backgroundColor: C.tile, objectFit: 'contain' },
  thumbEmpty: { width: 46, height: 34, marginRight: 9, borderRadius: 4, backgroundColor: C.tile },
  nameCol: { flex: 1, paddingRight: 8 },
  name: { fontSize: 10, fontWeight: 700, lineHeight: 1.25 },
  tag: {
    alignSelf: 'flex-start',
    marginTop: 3,
    paddingHorizontal: 5,
    paddingVertical: 1.5,
    borderRadius: 6,
    backgroundColor: C.greenTint,
    color: C.greenInk,
    fontSize: 6.5,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  value: { fontSize: 11, fontWeight: 700, textAlign: 'center' },
  unit: { fontSize: 7.5, fontWeight: 500, color: C.ink2 },
  session: { width: 58, height: 28, marginLeft: 6, borderWidth: 0.6, borderColor: C.field, borderRadius: 4 },
  // How-to page
  howGrid: { flexDirection: 'row', justifyContent: 'space-between' },
  howCol: { width: '48.5%' },
  howItem: { flexDirection: 'row', marginBottom: 12 },
  howThumb: { width: 40, height: 30, marginRight: 8, borderRadius: 3, backgroundColor: C.tile, objectFit: 'contain' },
  howBody: { flex: 1 },
  howName: { fontSize: 9.5, fontWeight: 700, marginBottom: 3 },
  howStep: { flexDirection: 'row', marginBottom: 1.5 },
  howStepNum: { width: 10, fontSize: 7.5, fontWeight: 700, color: C.green },
  howStepText: { flex: 1, fontSize: 7.5, lineHeight: 1.35, color: C.ink2 },
});

const COLS = { sets: 34, reps: 34, load: 46, rest: 40 };

const getLabels = (language) =>
  language === 'pt'
    ? {
        brand: 'Meu Treino · Ficha de treino',
        workout: 'Treino',
        all: 'Meus treinos',
        exercise: 'Exercício',
        sets: 'Séries',
        reps: 'Reps',
        load: 'Carga',
        rest: 'Desc.',
        session: (n) => `Sessão ${n}`,
        warmup: 'Aquecimento',
        student: 'Aluno',
        start: 'Início',
        howTo: 'Como executar',
        howToSub: 'O passo a passo de cada exercício da ficha',
        meta: (count, sets, minutes) => `${count} exercícios · ${sets} séries · ~${minutes} min`,
        generated: (date) => `Ficha gerada em ${date}`,
        page: (n, total) => `Página ${n} de ${total}`,
        site: 'meutreino.fit',
      }
    : {
        brand: 'My Workout · Gym sheet',
        workout: 'Workout',
        all: 'My workouts',
        exercise: 'Exercise',
        sets: 'Sets',
        reps: 'Reps',
        load: 'Load',
        rest: 'Rest',
        session: (n) => `Session ${n}`,
        warmup: 'Warm-up',
        student: 'Name',
        start: 'Start date',
        howTo: 'How to do it',
        howToSub: 'Step by step for every exercise on the sheet',
        meta: (count, sets, minutes) => `${count} exercises · ${sets} sets · ~${minutes} min`,
        generated: (date) => `Sheet made on ${date}`,
        page: (n, total) => `Page ${n} of ${total}`,
        site: 'myworkoutskill.com',
      };

const formatRest = (seconds) => {
  const total = Number(seconds) || 0;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const isWarmup = (exercise) => !!exercise.warmup || WARMUP_SUFFIX.test(exercise.name || '');
const baseName = (exercise) => (exercise.name || '').replace(WARMUP_SUFFIX, '').trim();

// Sets × (reps × rest + ~35 s of work per set), the same rough estimate the app shows
const estimateMinutes = (exercises) =>
  Math.max(
    5,
    Math.round(
      exercises.reduce((total, exercise) => total + (parseInt(exercise.series) || 0) * ((Number(exercise.interval) || 60) + 35), 0) / 60 / 5
    ) * 5
  );

const steps = (howTo) =>
  (howTo || '')
    .split('\n')
    .map((line) => line.replace(/^\s*\d+[.)]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 6);

const Thumb = ({ url, style, emptyStyle }) => (url ? <Image src={pdfImage(url)} style={style} /> : <View style={emptyStyle || style} />);

const TableHead = ({ L }) => (
  <View style={s.headRow} fixed>
    <Text style={[s.th, { width: 18 }]}>#</Text>
    <Text style={[s.th, { width: 55 }]} />
    <Text style={[s.th, { flex: 1 }]}>{L.exercise}</Text>
    <Text style={[s.th, s.thCenter, { width: COLS.sets }]}>{L.sets}</Text>
    <Text style={[s.th, s.thCenter, { width: COLS.reps }]}>{L.reps}</Text>
    <Text style={[s.th, s.thCenter, { width: COLS.load }]}>{L.load}</Text>
    <Text style={[s.th, s.thCenter, { width: COLS.rest }]}>{L.rest}</Text>
    {Array.from({ length: SESSIONS }, (_, i) => (
      <View key={i} style={s.thSession}>
        <Text style={s.th}>{L.session(i + 1)}</Text>
        <View style={s.thDate} />
      </View>
    ))}
  </View>
);

const ExerciseRow = ({ exercise, index, L }) => (
  <View style={s.row} wrap={false}>
    <Text style={s.num}>{index + 1}</Text>
    <Thumb url={exercise.thumbnailUrl} style={s.thumb} emptyStyle={s.thumbEmpty} />
    <View style={s.nameCol}>
      <Text style={s.name}>{baseName(exercise)}</Text>
      {isWarmup(exercise) ? <Text style={s.tag}>{L.warmup}</Text> : null}
    </View>
    <Text style={[s.value, { width: COLS.sets }]}>{exercise.series || '–'}</Text>
    <Text style={[s.value, { width: COLS.reps }]}>{exercise.reps || '–'}</Text>
    <Text style={[s.value, { width: COLS.load }]}>
      {exercise.weight ? (
        <>
          {exercise.weight}
          <Text style={s.unit}> {exercise.weightUnit || 'kg'}</Text>
        </>
      ) : (
        '–'
      )}
    </Text>
    <Text style={[s.value, { width: COLS.rest }]}>{formatRest(exercise.interval)}</Text>
    {Array.from({ length: SESSIONS }, (_, i) => (
      <View key={i} style={s.session} />
    ))}
  </View>
);

// One how-to per distinct exercise: a warm-up repeats its main lift
const HowToPage = ({ exercises, title, L, footer }) => {
  const seen = new Set();
  const items = exercises.filter((exercise) => {
    const key = baseName(exercise).toLowerCase();
    if (!steps(exercise.howTo).length || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!items.length) return null;
  const half = Math.ceil(items.length / 2);

  const Item = ({ exercise }) => (
    <View style={s.howItem} wrap={false}>
      <Thumb url={exercise.thumbnailUrl} style={s.howThumb} />
      <View style={s.howBody}>
        <Text style={s.howName}>{baseName(exercise)}</Text>
        {steps(exercise.howTo).map((step, i) => (
          <View key={i} style={s.howStep}>
            <Text style={s.howStepNum}>{i + 1}</Text>
            <Text style={s.howStepText}>{step}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <Page size="A4" orientation="landscape" style={s.page}>
      <SheetHeader brand={L.brand} title={L.howTo} subtitle={title} meta={L.howToSub} />
      <View style={s.howGrid}>
        <View style={s.howCol}>
          {items.slice(0, half).map((exercise, i) => (
            <Item key={i} exercise={exercise} />
          ))}
        </View>
        <View style={s.howCol}>
          {items.slice(half).map((exercise, i) => (
            <Item key={i} exercise={exercise} />
          ))}
        </View>
      </View>
      {footer}
    </Page>
  );
};

const WorkoutPages = ({ name, description, exercises, L, language }) => {
  const title = `${L.workout} ${name}`;
  const totalSets = exercises.reduce((total, exercise) => total + (parseInt(exercise.series) || 0), 0);
  const footer = <SheetFooter site={L.site} generated={L.generated(today(language))} page={L.page} />;

  return (
    <>
      <Page size="A4" orientation="landscape" style={s.page}>
        <SheetHeader
          brand={L.brand}
          title={title}
          subtitle={description}
          meta={L.meta(exercises.length, totalSets, estimateMinutes(exercises))}
          fields={[L.student, L.start]}
          fixed
        />
        <TableHead L={L} />
        {exercises.map((exercise, index) => (
          <ExerciseRow key={index} exercise={exercise} index={index} L={L} />
        ))}
        {footer}
      </Page>
      <HowToPage exercises={exercises} title={title} L={L} footer={footer} />
    </>
  );
};

export const WorkoutPDF = ({ workoutData, language }) => {
  const L = getLabels(language);
  const { name, description, exercises = [] } = workoutData;

  return (
    <Document title={`${L.workout} ${name}`} author={L.site}>
      <WorkoutPages name={name} description={description} exercises={exercises} L={L} language={language} />
    </Document>
  );
};

export const AllWorkoutsPDF = ({ workouts, language }) => {
  const L = getLabels(language);

  return (
    <Document title={L.all} author={L.site}>
      {workouts.map((workout) => (
        <WorkoutPages
          key={workout.id}
          name={workout.attributes.name}
          description={workout.attributes?.description}
          exercises={workout.attributes?.exercises || []}
          L={L}
          language={language}
        />
      ))}
    </Document>
  );
};
